export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export const MIN_WIDTH = 640;
export const MIN_HEIGHT = 360;

/** Keeps at least a sliver of the title bar reachable, whatever the viewport size. */
export function clampToViewport(rect: Rect, viewport: Viewport): Rect {
  const w = Math.min(rect.w, viewport.width);
  const h = Math.min(rect.h, viewport.height);
  const x = Math.min(Math.max(rect.x, 0), Math.max(0, viewport.width - w));
  const y = Math.min(Math.max(rect.y, 0), Math.max(0, viewport.height - h));
  return { x, y, w, h };
}

export function applyDrag(rect: Rect, dx: number, dy: number, viewport: Viewport): Rect {
  return clampToViewport({ ...rect, x: rect.x + dx, y: rect.y + dy }, viewport);
}

export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export function applyResize(rect: Rect, edge: ResizeEdge, dx: number, dy: number, viewport: Viewport): Rect {
  let { x, y, w, h } = rect;

  if (edge.includes('e')) w = Math.max(MIN_WIDTH, rect.w + dx);
  if (edge.includes('s')) h = Math.max(MIN_HEIGHT, rect.h + dy);
  if (edge.includes('w')) {
    const newW = Math.max(MIN_WIDTH, rect.w - dx);
    x = rect.x + (rect.w - newW);
    w = newW;
  }
  if (edge.includes('n')) {
    const newH = Math.max(MIN_HEIGHT, rect.h - dy);
    y = rect.y + (rect.h - newH);
    h = newH;
  }

  return clampToViewport({ x, y, w, h }, viewport);
}

export function defaultRect(viewport: Viewport, kindIndex = 0): Rect {
  const w = Math.min(Math.max(viewport.width - 120, MIN_WIDTH), 1100);
  const h = Math.min(Math.max(viewport.height - 120, MIN_HEIGHT), 720);
  const offset = (kindIndex % 5) * 24;
  return clampToViewport({ x: 60 + offset, y: 40 + offset, w, h }, viewport);
}

/** Шаг смещения между соседними окнами при «Разложить каскадом». */
const CASCADE_STEP = 32;

/** `index` — позиция окна среди всех раскладываемых (порядок открытия), не `kindIndex`. Размер
 *  окна при каскаде не меняется (значения близки к `defaultRect`), меняется только смещение —
 *  так из каскада видно заголовок каждого окна по диагонали. */
export function cascadeRect(index: number, viewport: Viewport): Rect {
  const w = Math.min(Math.max(viewport.width - 160, MIN_WIDTH), 1000);
  const h = Math.min(Math.max(viewport.height - 160, MIN_HEIGHT), 640);
  const maxOffsetX = Math.max(viewport.width - w, 0);
  const maxOffsetY = Math.max(viewport.height - h, 0);
  const maxSteps = Math.max(1, Math.floor(Math.min(maxOffsetX, maxOffsetY) / CASCADE_STEP));
  const offset = (index % (maxSteps + 1)) * CASCADE_STEP;
  return clampToViewport({ x: 40 + offset, y: 32 + offset, w, h }, viewport);
}

/** Раскладывает `total` окон плиткой `cols × rows` без зазоров (`cols = ceil(sqrt(total))`) —
 *  последний ряд может оказаться неполным, остаток viewport'а просто остаётся пустым. При
 *  большом `total` ячейка может выйти меньше `MIN_WIDTH`/`MIN_HEIGHT` — это осознанно (плитка
 *  обязана показать все окна целиком), ручной ресайз после этого уже не даст сделать её меньше
 *  минимума. */
export function tileRect(index: number, total: number, viewport: Viewport): Rect {
  const cols = Math.max(1, Math.ceil(Math.sqrt(total)));
  const rows = Math.max(1, Math.ceil(total / cols));
  const col = index % cols;
  const row = Math.floor(index / cols);
  const w = Math.floor(viewport.width / cols);
  const h = Math.floor(viewport.height / rows);
  return clampToViewport({ x: col * w, y: row * h, w, h }, viewport);
}
